import { useEffect, useMemo, useRef, useState } from "react"
import EvaluationMode from "./EvaluationMode";
import AgreementMode from "./AgreementMode";
import { useDispatch, useSelector } from "react-redux";
import { getIsDirty, getLang, getModule, setIsDirty } from "store/userSlice";
import { fetchFields, getCalculationRelatedFields, getConditionalFields, getFixedAndBalancedScorecardFields, getFixedFields, getFreeFields, getSystemFields } from "store/fieldsSlice";
import { fetchInterviewsSettings, getComplaintEnabled, getInterviewsSettings } from "store/interviewsSettingsSlice";


import { emptyHtml, errorMessage, fileDownloadResponse, formatDateTime, formatMoney, formatNumberTo, isTrue, leaveConfirm, navHeight, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { toast } from "helpers/toast";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { useFieldArray, useForm } from 'react-hook-form';

import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

import infoIcon from 'assets/images/icons/info.png';
import personIcon from 'assets/images/icons/person.png';
import peopleIcon from 'assets/images/icons/people.png';
import toolIcon from 'assets/images/icons/tool.png';

import sanitize from "helpers/sanitize";
import InterviewMode from "./InterviewMode";
import { BalancedScorecardFieldValue } from "./BalancedScorecardFieldValue";
import { getGeneralSettings } from "store/generalSettingsSlice";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import EditorInput from "components/ui/EditorInput";
import ExpandableHtml from "components/ui/ExpandableHtml";
import StateEmployeeDataMode from "./DB-EmployeeDataMode";
import { useLocation } from "react-router-dom";
import StateEmployeeDataCompactReadOnly from "./DB-EmployeeDataCompactReadOnly";
import LineClamp from "components/ui/LineClamp";
import { isDbModule } from "helpers/module";
import { Button } from "components/ui";

function StateEmployeeObjectivesCompactRow({ data, balancedScorecardDetails, isLast = false }) {
  const savedSystematicObjective = useMemo(() => {
    if (balancedScorecardDetails?.agreement_data?.systematicObjectives && Array.isArray(balancedScorecardDetails?.agreement_data?.systematicObjectives)) {
      const filtered = balancedScorecardDetails.agreement_data.systematicObjectives.filter(o => o.name === data.systematicObjectiveGridName);

      if (filtered.length > 0) {
        let agreed = filtered[0].agreed;

        if (agreed) return { ...filtered[0] };
      }
    }
    return null;
  }, [balancedScorecardDetails]);

  const savedValue = useMemo(() => {
    if (!savedSystematicObjective || !savedSystematicObjective?.value) return null;

    return savedSystematicObjective?.level?.replace('/', ' / ');
  }, [savedSystematicObjective]);

  if (savedSystematicObjective?.skipped) return null;

  return <div className={`row ${!isLast ? 'mb-3' : ''}`}>
    <div className="col-8 font-weight-bold">
      <LineClamp text={data.systematicObjectiveGridName} />
    </div>
    <div className="col-4">
      <span>{savedValue}</span>
    </div>
  </div>;
}

function StateEmployeeObjectivesCompactReadOnly({ settings, balancedScorecardDetails, initialEvaluationCompleted = false }) {
  const { editableTranslate } = useTranslation();

  return <>
    {
      !initialEvaluationCompleted && <div>
        {editableTranslate('state_employee.rating_card_not_completed')}
      </div>
    }
    {
      (initialEvaluationCompleted && settings?.systematicObjectiveDatagrid) && settings?.systematicObjectiveDatagrid.map((item, i) => <StateEmployeeObjectivesCompactRow
        key={i}
        isLast={i === settings?.systematicObjectiveDatagrid.length - 1}
        data={item}
        balancedScorecardDetails={balancedScorecardDetails} />)
    }
  </>;
}

function buildSnapshotBackedEmployee(employee, contextSnapshot) {
  if (!employee || !contextSnapshot) return employee;

  const employeePeriodData = contextSnapshot?.employee_period_data || {};
  const employeeInformation = Array.isArray(contextSnapshot?.employee_information)
    ? contextSnapshot.employee_information
    : [];

  const information = employeeInformation.reduce((obj, item) => {
    if (item?.key) obj[item.key] = item.value ?? "";
    return obj;
  }, {});

  return {
    ...employee,
    ...employeePeriodData,
    pay_grade_label: employeePeriodData?.pay_grade === employee?.pay_grade
      ? employee?.pay_grade_label
      : null,
    information,
    employeeInformation: {
      ...(employee?.employeeInformation || {}),
      data: employeeInformation.map((item) => ({
        key: item?.key ?? null,
        value: item?.value ?? null,
        resolved_label: item?.resolved_label ?? null,
        translation_key: item?.translation_key ?? null,
      })),
    },
  };
}

export default function Content({ show, formData = null, employee = null, balancedScorecard = null, fetchBalancedScorecards = null, reviewerView = false, allowReportEmail = true, setContentDirty = null, fetchEmployee = null, isModal = false, isPreview = false }) {
  const dispatch = useDispatch();

  const location = useLocation();

  const isSecondaryEvaluator = useMemo(() => {
    return location.pathname.includes('/secondary-');
  }, [location]);

  const lang = useSelector(getLang);
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const currentModule = useSelector(getModule);

  const [previewMode, setPreviewMode] = useState('evaluation');

  const generalSettings = useSelector(getGeneralSettings);

  const fixedFields = useSelector(getFixedAndBalancedScorecardFields);
  const freeFields = useSelector(getFreeFields);
  const systemFields = useSelector(getSystemFields);
  const conditionalFields = useSelector(getConditionalFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);

  const interviews = useSelector(getInterviewsSettings);
  const complaintEnabled = useSelector(getComplaintEnabled);

  const [interviewEnabled, setInterviewEnabled] = useState(true);

  const [error, setError] = useState(null);

  const [errorField, setErrorField] = useState(null);

  const errorRef = useRef(null);

  const [lkDetailsFetched, setLkDetailsFetched] = useState(false);

  const [balancedScorecardDetails, setBalancedScorecardDetails] = useState(null);
  const [settings, setSettings] = useState(formData);

  const [evaluationCompleted, setEvaluationCompleted] = useState(false);

  const [weight, setWeight] = useState({});

  const [assignedTeamObjectives, setAssignedTeamObjectives] = useState(null);
  const [individualObjectives, setIndividualObjectives] = useState(null);

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [feedbackError, setFeedbackError] = useState(false);

  const [showDirection, setShowDirection] = useState(false);

  const [downloadFull, setDownloadFull] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);

  const [editFeedback, setEditFeedback] = useState(false);

  const [displayImportOptions, setDisplayImportOptions] = useState(false);
  const [importObjectives, setImportObjectives] = useState({});
  const [importObjectivesForm, setImportObjectivesForm] = useState({});

  const [customConfigFetched, setCustomConfigFetched] = useState(false);

  const [customConfig, setCustomConfig] = useState(null);

  const [balancedScorecardMetas, setBalancedScorecardMetas] = useState({});

  const globalIsDirty = useSelector(getIsDirty);

  const [agreementDirty, setAgreementDirty] = useState(false);
  const [evaluationDirty, setEvaluationDirty] = useState(false);
  const [interviewDirty, setInterviewDirty] = useState(false);
  const [stateEmployeeDirty, setStateEmployeeDirty] = useState(false);

  const [completeLoading, setCompleteLoading] = useState(false);

  const [interviewsData, setInterviewsData] = useState([]);

  const [complaints, setComplaints] = useState([]);

  const [countEvaluation, setCountEvaluation] = useState(0);

  const [compactDataLoaded, setCompactDataLoaded] = useState(false);

  const [defaultLogoExist, setDefaultLogoExist] = useState(null);

  const compactRowRef = useRef();

  const samples = {
    employee_number: 'P23448G',
    project: 'Hauptamt',
    group: 'Ordnungsamt',
    category: 'Beschäftigte',
    pay_factor: 3938,
    working_hours: 39
  };

  const softwareSettings = useMemo(() => {
    const obj = {};

    if (generalSettings) {
      generalSettings.forEach(setting => {
        obj[setting.key] = setting.value;
      });
    }

    return obj;
  }, [generalSettings]);

  const checkConfig = (key, withData = false) => {
    const enabled = !!(customConfig && customConfig[key] && customConfig[key].enabled === true);
    if (!withData) return enabled;
    return { enabled, custom_data: enabled ? (customConfig[key].custom_data ?? null) : null };
  };

  const initialEvaluationCompleted = useMemo(() => {
    return balancedScorecardDetails?.initial_evaluation_completed_at;
  }, [balancedScorecardDetails]);

  const scorecardContextEmployee = useMemo(() => {
    return buildSnapshotBackedEmployee(employee, balancedScorecardDetails?.context_snapshot);
  }, [employee, balancedScorecardDetails?.context_snapshot]);

  const scorecardContextFrozen = useMemo(() => {
    return !!balancedScorecardDetails?.context_snapshot;
  }, [balancedScorecardDetails?.context_snapshot]);

  const scorecardContextEditable = useMemo(() => {
    return !!balancedScorecardDetails?.scorecard_context_editable;
  }, [balancedScorecardDetails?.scorecard_context_editable]);

  const stateEvaluationFlow = useMemo(() => {
    return {
      'flow': balancedScorecardDetails?.state_evaluation_flow,
      'current_step': balancedScorecardDetails?.state_current_evaluation_step
    };
  }, [balancedScorecardDetails]);

  const canOpenInterviewTab = useMemo(() => {
    if (!stateEvaluationFlow?.flow || !stateEvaluationFlow?.current_step) return true;

    if (!isSecondaryEvaluator && stateEvaluationFlow?.current_step === 'primary_meeting') return true;
    else if (isSecondaryEvaluator && stateEvaluationFlow?.current_step === 'secondary_meeting') return true;

    for (const interview of interviewsData) {
      if (interview?.is_completed) return true;
    }

    return false;
  }, [stateEvaluationFlow, isSecondaryEvaluator, interviewsData]);

  const readOnlyMode = useMemo(() => {
    return reviewerView || evaluationCompleted;
  }, [reviewerView, evaluationCompleted]);

  const filteredFields = useMemo(() => {
    const all = [];

    if (fixedFields) all.push(...fixedFields);
    if (conditionalFields) all.push(...conditionalFields);
    if (systemFields) all.push(...systemFields);
    if (calculationRelatedFields) all.push(...calculationRelatedFields);
    if (freeFields) all.push(...freeFields);

    return all.filter(f => f.is_active && f.display_in.includes('balanced_scorecard')).map(f => {
      return { ...f, key: f.translation_key.replace('field.', '') };
    });
  }, [fixedFields, conditionalFields, systemFields, calculationRelatedFields, freeFields]);

  const importObjectivesSynced = useMemo(() => {
    return {
      individual_objectives: !importObjectives?.individual_objectives ? [] : importObjectives.individual_objectives.filter(o => importObjectivesForm.individual_objective_hash_ids.includes(o.hash_id)),
      team_objectives: !importObjectives?.team_objectives ? [] : importObjectives.team_objectives.filter(o => importObjectivesForm.team_objective_hash_ids.includes(o.hash_id)),
      systematic_objectives: !importObjectives?.systematic_objectives ? [] : importObjectives.systematic_objectives.filter(o => importObjectivesForm.systematic_objective_names.includes(o.name))
    }
  }, [importObjectives, importObjectivesForm]);

  const [individualResultCommentMandatory, systematicResultCommentMandatory] = useMemo(() => {
    let ind = false;
    let sys = false;

    if (settings && balancedScorecardDetails) {
      let totalIndividual = balancedScorecardDetails?.agreement_data?.totalValue?.individual;
      if (totalIndividual) totalIndividual = +formatNumberTo(totalIndividual, '.');

      let indMin = settings?.individualMandatoryCommentExtremeRatingMinimum;
      if (indMin) indMin = +formatNumberTo(indMin, '.');
      let indMax = settings?.individualMandatoryCommentExtremeRatingMaximum;
      if (indMax) indMax = +formatNumberTo(indMax, '.');

      // true if total value individual not less than extreme minimum & not greater than extreme maximum
      ind = settings?.individualMandatoryCommentForExtremeRating === true && settings?.individualMandatoryCommentForExtremeRatingType === 'based_on_overall_result' && totalIndividual && (totalIndividual >= indMin && totalIndividual <= indMax);

      let totalSystematic = balancedScorecardDetails?.agreement_data?.totalValue?.systematic;
      if (totalSystematic) totalSystematic = +formatNumberTo(totalSystematic, '.');

      let sysMin = settings?.systematicMandatoryCommentExtremeRatingMinimum;
      if (sysMin) sysMin = +formatNumberTo(sysMin, '.');
      let sysMax = settings?.systematicMandatoryCommentExtremeRatingMaximum;
      if (sysMax) sysMax = +formatNumberTo(sysMax, '.');

      // true if total value systematic not less than extreme minimum & not greater than extreme maximum
      sys = settings?.systematicMandatoryCommentForExtremeRating === true && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_overall_result' && totalSystematic && (totalSystematic >= sysMin && totalSystematic <= sysMax);

      if (settings?.howToAppraisal?.includes('individual_and_systematic') && settings?.hasDifferentEvaluationLevel === false) {
        // using general
        sys = ind;
      }
    }

    return [ind, sys];
  }, [settings, balancedScorecardDetails]);

  const levels = useMemo(() => {
    const obj = {
      individual: (settings?.individualObjectivePerformanceLevelGrid ?? []).map(item => {
        const temp = {};
        for (const k in item) {
          temp[k.replace('individualObjective', '').toLocaleLowerCase()] = item[k];
        }
        return temp;
      }),
      systematic: (settings?.systematicObjectivePerformanceLevelGrid ?? []).map(item => {
        const temp = {};
        for (const k in item) {
          temp[k.replace('systematicObjective', '').toLocaleLowerCase()] = item[k];
        }
        return temp;
      })
    };
    if (settings?.howToAppraisal?.includes('individual_and_systematic') && settings?.hasDifferentEvaluationLevel === false) {
      obj.general = settings?.generalPerformanceLevelGrid?.map(item => {
        const temp = {};
        for (const k in item) {
          temp[k.replace('general', '').toLocaleLowerCase()] = item[k];
        }
        return temp;
      });
    }
    return obj;
  }, [settings]);

  const weighted = useMemo(() => {
    return settings?.performanceResultCalculation !== 'sum_of_all_unweighted';
  }, [settings]);

  const extremeLevels = useMemo(() => {
    const obj = {
      individual: levels?.individual ? levels?.individual?.filter(item => item['edgevalue'] === true).map(item => item['level']) : [],
      systematic: levels?.systematic ? levels?.systematic?.filter(item => item['edgevalue'] === true).map(item => item['level']) : []
    };

    if (levels?.general) {
      const arr = levels?.general?.filter(item => item['edgevalue'] === true).map(item => item['level']);
      obj.individual = [...arr];
      obj.systematic = [...arr];
    }

    return obj;
  }, [levels]);

  const isStateEmployee = useMemo(() => {
    if (employee && isDbModule(currentModule)) return true;
    return false;
  }, [employee, currentModule]);

  const viewMode = useMemo(() => {
    if (isPreview) return 'default';
    if (!balancedScorecard || !balancedScorecardDetails) return null;

    if (!isStateEmployee || (!isSecondaryEvaluator && !initialEvaluationCompleted)) return 'default';

    return 'compact';
  }, [isStateEmployee, balancedScorecard, isPreview, balancedScorecardDetails, initialEvaluationCompleted, isSecondaryEvaluator]);

  const [firstEvaluatorName, secondaryEvaluatorName] = useMemo(() => {
    let firstEv = balancedScorecardDetails?.group?.evaluators?.default?.name;
    if (balancedScorecardDetails?.initial_evaluation_completed_by) {
      firstEv = balancedScorecardDetails?.initial_evaluation_completed_by?.full_name;
    }

    let secondEv = balancedScorecardDetails?.group?.evaluators?.secondary?.name;
    if (balancedScorecardDetails?.evaluation_completed_by) {
      secondEv = balancedScorecardDetails?.evaluation_completed_by?.full_name;
    }

    return [firstEv, secondEv];
  }, [balancedScorecardDetails]);

  const isExcluded = useMemo(() => {
    return balancedScorecardDetails?.rating_mode === 'average_value_percentile' || employee?.exclude_from_payout;
  }, [employee, balancedScorecardDetails]);

  useEffect(() => {
    if (isStateEmployee && balancedScorecardDetails) {
      if (balancedScorecardDetails?.initial_evaluation_completed_at || balancedScorecardDetails?.has_evaluation_started) {
        setPreviewMode('evaluation');
      } else {
        setPreviewMode('state_employee_data');
      }
    }
  }, [isStateEmployee, balancedScorecardDetails]);

  useEffect(() => {
    if (!fixedFields || !freeFields) dispatch(fetchFields());

    if (!interviews) dispatch(fetchInterviewsSettings());

    // fetchFeatureFlags();
  }, []);

  useEffect(() => {
    if (show && employee && balancedScorecard) {
      fetchBalancedScorecardDetails(true);
      return;
    }
    setLkDetailsFetched(true);
    setCustomConfigFetched(true);

    setDownloadFull(false);
  }, [show, balancedScorecard]);

  useEffect(() => {
    if (!settings || show) setSettings(formData);
  }, [formData, show]);

  useEffect(() => {
    if (balancedScorecardDetails) {
      if (!isStateEmployee) {
        fetchAssignedTeamObjectives();
        fetchIndividualObjectives();
      }
      setSettings(balancedScorecardDetails.balanced_scorecard_setting?.settings);
      setEvaluationCompleted(balancedScorecardDetails?.evaluation_completed_at ? true : false);
      setWeight(balancedScorecardDetails.agreement_data?.weight || {});
      setFeedback(balancedScorecardDetails?.agreement_data?.feedback || null);

      if (balancedScorecardDetails.balanced_scorecard_setting?.form_definition.type.startsWith('custom')) {
        if (!customConfig) fetchCustomConfig();
      } else {
        setCustomConfigFetched(true);
      }
    }
  }, [isStateEmployee, balancedScorecardDetails]);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (setContentDirty) setContentDirty(!evaluationCompleted && !reviewerView && (agreementDirty || evaluationDirty || interviewDirty || stateEmployeeDirty));
  }, [evaluationCompleted, reviewerView, agreementDirty, evaluationDirty, interviewDirty, stateEmployeeDirty]);

  useEffect(() => {
    setFeedbackError(false);
  }, [editFeedback]);

  useEffect(() => {
    if (previewMode === 'report') checkDefaultLogo();
  }, [previewMode]);

  const checkDefaultLogo = async () => {
    try {
      const { data, status } = await api.get('/settings/report-logos');
      if (status === 200) {
        for (let i = 0; i < data.data.length; i++) {
          if (data.data[i].is_default) {
            try {
              const { data: data2 } = await api.get('/settings/report-logos/' + data.data[i].hash_id, {
                responseType: 'blob',
                headers: {
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache',
                  'Expires': '0'
                }
              });
              setDefaultLogoExist(true);
              return;
            } catch (innerErr) {

            }
            break;
          }
        }
        setDefaultLogoExist(false);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const alignCompactRow = (mutations = null) => {
    const row = compactRowRef?.current;
    const cols = row.querySelectorAll('.col-compact');

    for (let i = 0; i < cols.length; i += 2) {
      const col1 = cols[i];
      const col2 = cols[i + 1];

      if (!col1 || !col2) continue;

      const leftBoxs = col1.querySelectorAll('.gray-box-2');
      const rightBoxs = col2.querySelectorAll('.gray-box-2');

      if (leftBoxs.length < 1 || rightBoxs.length < 1) continue;

      const tempMinL = leftBoxs[0].style.minHeight;
      leftBoxs[0].style.minHeight = 'unset';
      let leftHeight = leftBoxs[0].offsetHeight;
      leftBoxs[0].style.minHeight = tempMinL;

      const tempMinR = rightBoxs[0].style.minHeight;
      rightBoxs[0].style.minHeight = 'unset';
      let rightHeight = rightBoxs[0].offsetHeight;
      rightBoxs[0].style.minHeight = tempMinR;

      if (leftBoxs.length > 1) {
        leftHeight = col1.offsetHeight - (col1.querySelector('.col-compact-header')?.offsetHeight || 0) - 7;
      }
      if (rightBoxs.length > 1) {
        rightHeight = col2.offsetHeight - (col2.querySelector('.col-compact-header')?.offsetHeight || 0) - 7;
      }

      if (col1.querySelector('.div-html-expanded') || col2.querySelector('.div-html-expanded')) continue;

      const maxHeight = Math.max(leftHeight, rightHeight);

      if (leftBoxs.length > 1) {
        let diff = maxHeight - leftHeight;
        let lHeight = Math.min(maxHeight, (leftBoxs[leftBoxs.length - 1].offsetHeight + diff));

        if (col1.querySelector('.div-html')) {
          leftBoxs[leftBoxs.length - 1].style.minHeight = lHeight + 'px';
        } else {
          leftBoxs[leftBoxs.length - 1].style.minHeight = lHeight + 'px';
        }
      } else {
        if (col1.querySelector('.div-html')) {
          leftBoxs[0].style.minHeight = maxHeight + 'px';
        } else {
          leftBoxs[0].style.minHeight = maxHeight + 'px';
        }
      }

      if (rightBoxs.length > 1) {
        let diff = maxHeight - rightHeight;
        let rHeight = Math.min(maxHeight, (rightBoxs[rightBoxs.length - 1].offsetHeight + diff));

        if (col2.querySelector('.div-html')) {
          rightBoxs[rightBoxs.length - 1].style.minHeight = rHeight + 'px';
        } else {
          rightBoxs[rightBoxs.length - 1].style.minHeight = rHeight + 'px';
        }
      } else {
        if (col2.querySelector('.div-html')) {
          rightBoxs[0].style.minHeight = maxHeight + 'px';
        } else {
          rightBoxs[0].style.minHeight = maxHeight + 'px';
        }
      }
    }
  };

  useEffect(() => {
    if (viewMode !== 'compact' || !compactRowRef?.current) return;

    const observer = new MutationObserver(alignCompactRow);

    observer.observe(compactRowRef.current, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'compact') return;

    if (countEvaluation > 0) {
      alignCompactRow();
    }
  }, [viewMode, countEvaluation]);

  useEffect(() => {
    if (compactRowRef?.current) compactRowRef.current.querySelector('.hidden-compact-div').textContent = (balancedScorecardDetails?.hash_id || '-');
  }, [compactRowRef, balancedScorecardDetails]);

  const fetchCustomConfig = async () => {
    setError(null);
    try {
      const { data, status } = await api.get(`/settings/balanced-scorecards/${balancedScorecard.balanced_scorecard_setting.hash_id}/custom-config`);
      if (status === 200) {
        setCustomConfig(data.data.custom_config)
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch custom config'));
      Sentry.captureException(err);
    }
    setCustomConfigFetched(true);
  };

  const fetchBalancedScorecardDetails = async (loading = true) => {
    setError(null);

    if (loading) {
      setBalancedScorecardDetails(null);
      setSettings(null);
      setEvaluationCompleted(false);
    }

    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/${balancedScorecard.hash_id}` + (reviewerView ? `?as=reviewer` : ''));
      if (status === 200) {
        setBalancedScorecardDetails(data);
        setInterviewEnabled(data?.balanced_scorecard_setting?.custom_config?.interview_enabled ?? true);
        setBalancedScorecardMetas({ ...data.meta });
        fetchInterviewsData();
        fetchComplaints();

        if (data.action === 'objectives_import_confirmation') {
          setPreviewMode('import_confirmation');
          setDisplayImportOptions(false);
        } else if (previewMode === 'import_confirmation') {
          setPreviewMode('evaluation');
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch balanced scorecard details'));
      Sentry.captureException(err);
    }
    setLkDetailsFetched(true);
  };

  const fetchInterviewsData = async () => {
    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/${balancedScorecard.hash_id}/interviews` + (reviewerView ? `?as=reviewer` : ''));
      if (status === 200) {
        setInterviewsData(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch balanced scorecard interviews'));
      Sentry.captureException(err);
    }
  };

  const fetchComplaints = async () => {
    try {
      const { data, status } = await api.get(`/employee-complaints/employee-balanced-scorecard/${balancedScorecard.hash_id}`);
      if (status === 200) {
        setComplaints(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch balanced scorecard complaints'));
      Sentry.captureException(err);
    }
  };

  const handleCompleteEvaluation = async (e, shouldConfirm = true, bsData = null) => {
    if (!employee) return;

    setError(null);
    setCompleteLoading(true);

    const types = [];
    if (settings?.howToAppraisal.includes('individual')) types.push('individual');
    if (settings?.howToAppraisal.includes('systematic')) types.push('systematic');

    if (!validateAll(bsData ? bsData : undefined)) {
      handleChangePreviewMode('evaluation', shouldConfirm);
      setCompleteLoading(false);
      if (isModal) {
        scrollToTop(document.querySelector('.preview-balance-scorecard-modal .modal-body'));
      } else {
        scrollToTop();
      }
      return;
    }

    if (isStateEmployee && customConfig?.secondary_evaluation_enabled === true) {
      if (!isSecondaryEvaluator && !initialEvaluationCompleted) {
        await completeInitialEvaluation();
        setCompleteLoading(false);
        return;
      }
    }

    setEvaluationCompleted(true);

    await saveEvaluation({ 'completed': true }, true);
    setCompleteLoading(false);
  };

  const isValid100 = (x) => {
    return x === 100 || Math.abs(x - 100) < 0.0001;
  };

  const isSystematicObjExistInSetting = (name, subName = null) => {
    if (isTrue(settings?.shouldHasSubCriteria)) {
      for (const obj of settings?.systematicObjectiveDatagrid_subCriteria) {
        if (obj?.systematicObjectiveGridName === name) {
          if (!subName) return true;

          for (const subObj of obj?.systematicObjectivesubCriteria) {
            if (subObj?.systematicSubObjectiveGridName === subName) return true;
          }
        }
      }
    } else {
      for (const obj of settings?.systematicObjectiveDatagrid) {
        if (obj?.systematicObjectiveGridName === name) return true;
      }
    }

    return false;
  };

  const weightingErrorTranslationKey = useMemo(() => {
    if (!balancedScorecardDetails ||
      !weighted ||
      !settings ||
      checkConfig('balanced-scorecard-layout-v2') !== false ||
      checkConfig('balanced-scorecard-layout-v3') !== false) return null;

    let totalWeight = 0;
    const tempWeight = balancedScorecardDetails?.agreement_data?.weight;
    for (const k in tempWeight) { totalWeight += parseFloat(formatNumberTo(tempWeight[k], '.')); }
    if (totalWeight !== 100) {
      return 'evaluation.validation.total_weight';
    }

    if (settings?.howToAppraisal.includes('individual') && parseFloat(formatNumberTo(tempWeight?.individual, '.')) > 0 && individualObjectives && assignedTeamObjectives) {
      let totalIndividualWeight = 0;
      individualObjectives?.forEach(obj => totalIndividualWeight += (obj?.weight ? parseFloat(formatNumberTo(obj.weight, '.')) : 0));
      assignedTeamObjectives?.forEach(obj => totalIndividualWeight += (obj.weight ? parseFloat(formatNumberTo(obj.weight, '.')) : 0));

      if (!balancedScorecardDetails?.agreement_data?.individualObjectives || balancedScorecardDetails?.agreement_data?.individualObjectives?.length === 0) {
        return 'evaluation.no_individual_objectives_in_lk';
      }

      if (totalIndividualWeight !== 100) {
        return 'evaluation.validation.total_weight_individual';
      }
    }

    if (settings?.howToAppraisal.includes('systematic') && parseFloat(formatNumberTo(tempWeight?.systematic, '.')) > 0) {
      let totalSystematicWeight = 0;
      for (const obj of balancedScorecardDetails?.agreement_data?.systematicObjectives) {
        if (!isSystematicObjExistInSetting(obj?.name)) continue;

        if (obj.agreed) {
          totalSystematicWeight += (obj?.weight ? parseFloat(formatNumberTo(obj.weight, '.')) : 0);

          if (obj.sub) {
            let totalSub = 0;
            for (const s of obj.sub) {
              if (!isSystematicObjExistInSetting(obj?.name, s?.name)) continue;

              if (s.agreed) totalSub += (s?.weight ? parseFloat(formatNumberTo(s.weight, '.')) : 0);
            }

            if (!isValid100(totalSub)) {
              return 'evaluation.validation.total_weight_criteria';
            }
          }
        }
      }

      if (!isValid100(totalSystematicWeight)) {
        return 'evaluation.validation.total_weight_systematic';
      }
    }

    return null;
  }, [balancedScorecardDetails, weighted, settings, individualObjectives, assignedTeamObjectives, customConfig]);

  const validateAll = (bsData = balancedScorecardDetails) => {
    // check weight
    let totalWeight = 0;
    for (const k in weight) { totalWeight += parseFloat(formatNumberTo(weight[k], '.')); }

    if (weighted && totalWeight !== 100) {
      setError({ translationKey: 'evaluation.validation.total_weight' });
      setErrorField({ totalWeight: true });
      return false;
    }

    // check individual objectives weight
    if (settings?.howToAppraisal.includes('individual') && (individualObjectives?.length > 0 || assignedTeamObjectives?.length > 0)) {
      if (weighted) {
        let totalIndividualWeight = 0;
        individualObjectives?.forEach(obj => totalIndividualWeight += parseFloat(formatNumberTo(obj.weight, '.')));
        assignedTeamObjectives?.forEach(obj => totalIndividualWeight += parseFloat(formatNumberTo(obj.weight, '.')));

        if (totalIndividualWeight !== 100) {
          setError({ translationKey: 'evaluation.validation.total_weight_individual' });
          setErrorField({ individual: { totalWeight: true } });
          return false;
        }
      }

      // when there's no individual objectives in the agreement data, mean that the user didn't do any evaluation
      if (!bsData.agreement_data.individualObjectives || bsData.agreement_data.individualObjectives.length === 0) {
        setError({ translationKey: 'evaluation.no_individual_objectives_in_lk' });
        setErrorField({ individual: { noObjective: true } });
        return false;
      }

      for (const obj of bsData.agreement_data.individualObjectives) {
        if (!obj.level) {
          setError({ translationKey: 'evaluation.validation.evaluation_level' });
          setErrorField({ individual: { objective: { hash_id: obj?.hash_id, level: true } } });
          return false;
        }
      }
    }

    if (settings?.howToAppraisal.includes('individual')) {
      if (settings?.individualMandatoryCommentForExtremeRating === true && settings?.individualMandatoryCommentForExtremeRatingType === 'based_on_single_objective') {
        for (const obj of bsData.agreement_data.individualObjectives) {
          if (obj.type === 'individual' && extremeLevels.individual.includes(obj.level) && emptyHtml(obj.comment)) {
            setError({ translationKey: 'evaluation.validation.individual_level_comment' });
            setErrorField({ individual: { objective: { hash_id: obj?.hash_id, comment: true } } });
            return false;
          }
        }
      }
    }

    // check systematic objectives weight
    if (settings?.howToAppraisal.includes('systematic')) {
      let totalSystematicWeight = 0;

      let systematicLevelMandatory = false;
      if (settings?.howToAppraisal?.includes('individual_and_systematic') && settings?.hasDifferentEvaluationLevel === false) {
        systematicLevelMandatory = (settings?.individualMandatoryCommentForExtremeRating === true && settings?.individualMandatoryCommentForExtremeRatingType === 'based_on_single_objective');
      } else {
        systematicLevelMandatory = (settings?.systematicMandatoryCommentForExtremeRating === true && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective');
      }

      for (const obj of bsData.agreement_data?.systematicObjectives) {
        if (!isSystematicObjExistInSetting(obj?.name)) continue;

        if (isStateEmployee) {
          if (obj?.skipped) {
            if (emptyHtml(obj?.comment)) {
              setError({ translationKey: 'evaluation.validation.systematic_level_comment' });
              setErrorField({ systematic: { objective: { name: obj?.name, comment: true } } });
              return false;
            } else {
              continue;
            }
          }
        }

        if (weighted && !obj.sub && obj.agreed && !obj.weight) {
          setError({ translationKey: 'evaluation.validation.systematic_weight' });
          setErrorField({ systematic: { objective: { name: obj?.name, weight: true } } });
          return false;
        }

        if (weighted && !obj.sub && obj.agreed) totalSystematicWeight += parseFloat(formatNumberTo(obj.weight, '.'));
        else if (obj.sub && obj.agreed) {
          let hasSubAgreed = false;
          let totalSub = 0;

          for (const s of obj.sub) {
            if (!isSystematicObjExistInSetting(obj?.name, s?.name)) continue;

            if (s.agreed && !s.level) {
              setError({ translationKey: 'evaluation.validation.evaluation_level' });
              setErrorField({ systematic: { objective: { name: obj?.name, sub: { name: s?.name, level: true } } } });
              return false;
            } else if (weighted && s.agreed && !s.weight) {
              setError({ translationKey: 'evaluation.validation.sub_systematic_weight' });
              setErrorField({ systematic: { objective: { name: obj?.name, sub: { name: s?.name, weight: true } } } });
              return false;
            }

            if (weighted && s.agreed && s.weight) {
              hasSubAgreed = true;
              totalSub += parseFloat(formatNumberTo(s.weight, '.'));
            }

            if (systematicLevelMandatory && s.agreed) {
              if (extremeLevels.systematic.includes(s.level) && emptyHtml(s.comment)) {
                setError({ translationKey: 'evaluation.validation.systematic_level_comment' });
                setErrorField({ systematic: { objective: { name: obj?.name, sub: { name: s?.name, comment: true } } } });
                return false;
              }
            }
          }

          if (weighted) {
            if (hasSubAgreed && !isValid100(totalSub)) {
              setError({ translationKey: 'evaluation.validation.total_weight_criteria' });
              setErrorField({ systematic: { objective: { name: obj?.name, totalWeight: true } } });
              return false;
            } else if (hasSubAgreed && isValid100(totalSub)) {
              totalSystematicWeight += parseFloat(formatNumberTo(obj.weight, '.'));
            }
          }
        }

        if (!obj.sub && obj.agreed && !obj.level) {
          setError({ translationKey: 'evaluation.validation.evaluation_level' });
          setErrorField({ systematic: { objective: { name: obj?.name, level: true } } });
          return false;
        }

        if (systematicLevelMandatory && obj.agreed) {
          if (extremeLevels.systematic.includes(obj.level) && emptyHtml(obj?.comment)) {
            setError({ translationKey: 'evaluation.validation.systematic_level_comment' });
            setErrorField({ systematic: { objective: { name: obj?.name, comment: true } } });
            return false;
          }
        }
      }

      if (weighted && !isValid100(totalSystematicWeight)) {
        setError({ translationKey: 'evaluation.validation.total_weight_systematic' });
        setErrorField({ systematic: { totalWeight: true } });
        return false;
      }
    }

    if (settings?.howToAppraisal.includes('individual')) {
      if (individualResultCommentMandatory && emptyHtml(bsData?.agreement_data?.comments?.individual)) {
        setError({ translationKey: 'evaluation.validation.individual_result_comment' });
        setErrorField({ individual: { result: { comment: true } } });
        return false;
      }
    }

    if (settings?.howToAppraisal.includes('systematic')) {
      if (systematicResultCommentMandatory && emptyHtml(bsData?.agreement_data?.comments?.systematic)) {
        setError({ translationKey: 'evaluation.validation.systematic_result_comment' });
        setErrorField({ systematic: { result: { comment: true } } });
        return false;
      }
    }

    if (isStateEmployee) {
      const firstLvl = bsData?.agreement_data?.overall_result?.first_evaluator?.level;
      const firstLvlVal = bsData?.agreement_data?.overall_result?.first_evaluator?.value;
      const secondLvl = bsData?.agreement_data?.overall_result?.second_evaluator?.level;
      const initialEvaluationEnabled = (customConfig?.initial_evaluation_enabled === true);

      if (!isSecondaryEvaluator && !initialEvaluationCompleted && initialEvaluationEnabled) { // first evaluator
        if (!firstLvl) {
          setError({ translationKey: 'evaluation.validation.evaluation_level' });
          setErrorField({ first_evaluator: { level: true } });
          return false;
        }

        const totalSystematic = parseFloat(formatNumberTo(bsData?.agreement_data?.totalValue?.systematic || -1, '.'));

        let isAdditionalJustificationRequired = false;
        const previousEvaluationResult = bsData?.employee?.previous_evaluation_results?.regular_evaluation?.result || null;
        if (previousEvaluationResult) {
          const validResult = levels?.systematic?.find(l => parseFloat(formatNumberTo(l.value, '.')) === parseFloat(formatNumberTo(previousEvaluationResult, '.')));
          if (validResult && parseFloat(formatNumberTo(firstLvlVal, '.')) !== parseFloat(formatNumberTo(previousEvaluationResult, '.'))) isAdditionalJustificationRequired = true;
        }

        if (
          (extremeLevels.systematic.includes(firstLvl)
            || (balancedScorecardMetas?.balanced_scorecard_suggested_levels_type === 'half_step' && totalSystematic % 1 === 0.5)
            || (parseFloat(formatNumberTo(firstLvlVal, '.')) !== Math.round(totalSystematic))
            || isAdditionalJustificationRequired)
          && emptyHtml(bsData?.agreement_data?.overall_result?.first_evaluator?.comment)) {

          setError({ translationKey: 'state_employee.first_evaluator_level_comment' });
          setErrorField({ first_evaluator: { comment: true } });
          return false;
        }
      } else if (isSecondaryEvaluator && initialEvaluationCompleted) { // second evaluator
        if (!secondLvl) {
          setError({ translationKey: 'evaluation.validation.evaluation_level' });
          setErrorField({ second_evaluator: { level: true } });
          return false;
        }

        if ((extremeLevels.systematic.includes(secondLvl) || secondLvl !== firstLvl)
          && emptyHtml(bsData?.agreement_data?.overall_result?.second_evaluator?.comment)) {
          setError({ translationKey: 'state_employee.second_evaluator_level_comment' });
          setErrorField({ second_evaluator: { comment: true } });
          return false;
        }
      }
    }

    if (!isStateEmployee && interviewEnabled && interviews?.length > 0) {
      const mandatoryInterviews = interviews.filter(i => i.is_mandatory);
      if (mandatoryInterviews.length > 0) {
        const mandatoryInterviewsIds = mandatoryInterviews.map(i => i.hash_id);
        const filteredInterviewsData = interviewsData.filter(i => mandatoryInterviewsIds.includes(i?.interview_setting?.hash_id) && i?.is_completed);

        if (mandatoryInterviews.length !== filteredInterviewsData.length) {
          setError(translate('evaluation.validation.interviews_not_completed') + ' (' + mandatoryInterviews.map(i => i.name).join(', ') + ')');
          setErrorField({ interview: true });
          return false;
        }
      }
    }

    if (complaintEnabled && complaints) {
      const unresolvedComplaints = complaints?.filter(c => !c.is_resolved);
      if (unresolvedComplaints.length > 0) {
        setError({ translationKey: 'evaluation.validation.complaint_not_resolved' });
        setErrorField({ complaint: true });
        return false;
      }
    }

    setError(null);
    setErrorField(null);

    return true;
  };

  const completeInitialEvaluation = async () => {
    setError(null);
    try {
      const { data, status } = await api.post(`/employee-balanced-scorecards/${balancedScorecard.hash_id}/complete-initial-evaluation`);
      if (status === 200) {
        setBalancedScorecardDetails(data);
        fetchBalancedScorecards(false);

        setPreviewMode('evaluation');

        toast.success(translate('form.success.saved'));

        setCountEvaluation(p => p + 1);
      }
    } catch (err) {
      setError(errorMessage(err, 'save complete initial evaluation'));
      Sentry.captureException(err);
    }
  };

  const saveEvaluation = async (evaluationData, completed = false) => {
    setError(null);

    const formData = {
      evaluation_data: evaluationData,
      completed
    };

    try {
      const { data, status } = await api.post(`/employee-balanced-scorecards/${balancedScorecard.hash_id}/evaluation`, formData);
      if (status === 200) {
        setBalancedScorecardDetails(data);
        fetchBalancedScorecards(false);

        if (completed) setPreviewMode('evaluation');

        toast.success(translate('form.success.saved'));

        setCountEvaluation(p => p + 1);
      }
    } catch (err) {
      setError(errorMessage(err, 'save balanced scorecard evaluation data'));
      Sentry.captureException(err);
    }
  };

  const saveAgreement = async (agreementData, showToast = true, callback = null) => {
    setError(null);

    const formData = {
      agreement_data: agreementData
    };

    try {
      const { data, status } = await api.post(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/agreement`, formData);
      if (status === 200) {
        setBalancedScorecardDetails(data);
        fetchBalancedScorecards(false);

        if (showToast) toast.success(translate('form.success.saved'));

        if (callback) {
          callback(data);
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'save balanced scorecard agreement data'));
      Sentry.captureException(err);
    }
  };

  const fetchAssignedTeamObjectives = async () => {
    setAssignedTeamObjectives(null);
    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/team-objectives` + (reviewerView ? `?as=reviewer` : ''));
      if (status === 200) {
        setAssignedTeamObjectives(data.data);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const fetchIndividualObjectives = async () => {
    setIndividualObjectives(null);
    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/individual-objectives` + (reviewerView ? `?as=reviewer` : ''));
      if (status === 200) {
        setIndividualObjectives(data.data);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const handleSaveFeedback = async () => {
    if (balancedScorecardDetails && feedback && !feedbackError) {
      let agreementData = balancedScorecardDetails?.agreement_data;
      if (!agreementData) agreementData = {};

      agreementData.feedback = feedback;

      setEditFeedback(false);

      await saveAgreement(agreementData);
    }
  };

  const handleCancelFeedback = () => {
    setFeedback(balancedScorecardDetails?.agreement_data?.feedback || null);

    setEditFeedback(false);
  };

  const handlePdfReport = async (email = false) => {
    if (!balancedScorecardDetails) return;

    setError(null);
    setPdfLoading(email ? 'email' : 'download');

    const config = {};
    if (!email) config.responseType = 'blob';

    try {
      const response = await api.post('/exports/employee-balanced-scorecard', {
        balanced_scorecard_hash_id: balancedScorecard.hash_id,
        full: downloadFull,
        email
      }, config);

      if (!email) fileDownloadResponse(response, isStateEmployee ? 'evaluation-report.pdf' : 'balanced-scorecard-report.pdf');
      else {
        toast.success(translate('form.export_email_success'));
      }

      setPdfLoading(false);
    } catch (err) {
      let errObj = { message: 'Something went wrong.' };

      // if blob has error
      if (err.response && err.response.data instanceof Blob) {
        try {
          console.log('trying to parse blob error');
          const text = await err.response.data.text();
          errObj = JSON.parse(text);
        } catch (e) {
          // ignore
        }
      } else if (err.response && err.response.data) {
        errObj = err.response.data;
      }

      setPdfLoading(false);
      setError(errorMessage(errObj, 'generate employee balanced scorecard PDF report'));
      Sentry.captureException(errObj);
    }
  };

  const handleChangePreviewMode = async (mode, shouldConfirm = true) => {
    if (globalIsDirty && shouldConfirm) {
      const confirmed = await leaveConfirm();
      if (!confirmed) return;
      else dispatch(setIsDirty(false));
    }
    if (balancedScorecardDetails?.action === 'objectives_import_confirmation') return;
    else setPreviewMode(mode);

    if (viewMode !== 'compact' && mode === 'complete_evaluation') {
      if (isModal) {
        scrollToTop(document.querySelector('.preview-balance-scorecard-modal .modal-body'));
      } else {
        scrollToTop();
      }
    }
  };

  const handleImportYes = () => {
    if (balancedScorecardDetails?.hash_id) getImportObjectives(balancedScorecardDetails?.hash_id);
    setDisplayImportOptions(true);
  };

  const handleImportNo = () => {
    saveImportObjectives({
      individual_objective_hash_ids: [],
      team_objective_hash_ids: [],
      systematic_objective_names: []
    });
  };

  const handleImportSave = () => {
    saveImportObjectives(importObjectivesForm);
  };

  const handleImportCancel = () => {
    setDisplayImportOptions(false);
  };

  const getImportObjectives = async (hash_id) => {
    setImportObjectives({});
    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/${hash_id}/import-objectives`);
      if (status === 200) {
        setImportObjectives(data);
        const formData = {
          individual_objective_hash_ids: data.individual_objectives.map(o => o.hash_id),
          team_objective_hash_ids: data.team_objectives.map(o => o.hash_id),
          systematic_objective_names: data.systematic_objectives.map(o => o.name)
        };
        setImportObjectivesForm(formData);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const saveImportObjectives = async (formData) => {
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${balancedScorecardDetails.hash_id}/import-objectives`, formData);

      if (status === 200) {
        fetchBalancedScorecardDetails(false);

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save import objectives'));
      Sentry.captureException(err);
    }
  };

  const handleRemoveImportObjective = (type, data) => {
    const formData = { ...importObjectivesForm };

    if (type === 'individual') {
      formData.individual_objective_hash_ids = formData.individual_objective_hash_ids.filter(o => o !== data);
    } else if (type === 'team') {
      formData.team_objective_hash_ids = formData.team_objective_hash_ids.filter(o => o !== data);
    } else if (type === 'systematic') {
      formData.systematic_objective_names = formData.systematic_objective_names.filter(o => o !== data);
    }

    setImportObjectivesForm(formData);
  };

  const handleBtnClickBlur = (e, cb) => {
    e?.currentTarget?.blur();
    cb();
  };

  const scrollToErrorField = (e) => {
    if (['i', 'button'].includes(e?.target?.tagName?.toLowerCase()) ||
      e?.target?.getAttribute('role') === 'button') return;

    if (errorField?.interview || errorField?.complaint) {
      handleChangePreviewMode('interview');
      return;
    }

    const invalidTr = document.querySelector('.invalid-tr') || document.querySelector('.is-invalid');
    if (!invalidTr) return;

    let offset = 50;
    if (errorRef?.current) {
      offset += errorRef?.current?.offsetHeight;
    }

    if (isModal) {
      const modalBody = document.querySelector('.preview-balance-scorecard-modal .modal-body');

      if (modalBody) {
        const top = invalidTr.getBoundingClientRect().top + (modalBody.scrollTop - 50) - offset;
        modalBody.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      let y = invalidTr.getBoundingClientRect().top + (window.scrollY - (navHeight() ?? 0)) - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleCloseError = () => {
    setError(null);
    setErrorField(null);
  };

  if (viewMode === null) return <></>;

  return <>
    {
      viewMode === 'default' && <div className="balanced-scorecard-content">
        {
          (customConfigFetched && !isExcluded) && <div className="d-flex justify-content-between align-items-center">
            <div>
              {
                isStateEmployee ?
                  <div className="btn-group btn-group-switch-mode" role="group">
                    <button type="button" className={`btn ${previewMode === 'state_employee_data' ? 'active' : ''}`} onClick={() => handleChangePreviewMode('state_employee_data')} data-testid="employee-data-tab">{editableTranslate('balanced_scorecard.state_employee_data_tab')}</button>
                    <button type="button" className={`btn ${previewMode === 'evaluation' ? 'active' : ''}`} onClick={() => handleChangePreviewMode('evaluation')} data-testid="evaluation-mode-button">{editableTranslate('balanced_scorecard.evaluation_mode')}</button>
                  </div> :
                  <div className="btn-group btn-group-switch-mode" role="group">
                    <button type="button" className={`btn ${previewMode === 'evaluation' ? 'active' : ''}`} onClick={() => handleChangePreviewMode('evaluation')} data-testid="evaluation-mode-button">{editableTranslate('balanced_scorecard.evaluation_mode')}</button>
                    {
                      checkConfig('balanced-scorecard-layout-v2') === false && <button type="button" className={`btn ${previewMode === 'agreement' ? 'active' : ''}`} onClick={() => handleChangePreviewMode('agreement')} data-testid="agreement-mode-tab">{editableTranslate('balanced_scorecard.agreement_mode')}</button>
                    }
                    {
                      (interviewEnabled && (interviews && interviews.length > 0)) && <button type="button" className={`btn ${previewMode === 'interview' ? 'active' : ''}`} onClick={() => handleChangePreviewMode('interview')} data-testid="interview-tab">{editableTranslate('global.menu.settings.interview')}</button>
                    }
                  </div>
              }
            </div>
            <div>
              {
                balancedScorecardMetas?.balanced_scorecard_feedback_enabled && <Button variant="secondary" onClick={(e) => handleBtnClickBlur(e, () => setShowFeedback(!showFeedback))} data-testid="interview-notes-button" className="mr-2"><i className={`bi ${!showFeedback ? 'bi-eye' : 'bi-eye-slash'}`}></i> {editableTranslate('interview.interview_notes')}</Button>
              }

              {
                balancedScorecardMetas?.balanced_scorecard_direction_enabled && <Button variant="secondary" onClick={(e) => handleBtnClickBlur(e, () => setShowDirection(!showDirection))} data-testid="directions-button" className="mr-2"><i className={`bi ${!showDirection ? 'bi-eye' : 'bi-eye-slash'}`}></i> {editableTranslate('balanced_scorecard.directions_button')}</Button>
              }

              <ReactTooltip id={`state-employee-evaluation-completed`} place="bottom-end">
                <span dangerouslySetInnerHTML={{
                  __html: sanitize(balancedScorecardDetails?.evaluation_completed_at ?
                    editableTranslateString('state_employee.rating_card_completed_detail').replace('$X', formatDateTime(balancedScorecardDetails?.evaluation_completed_at)) :
                    editableTranslateString('state_employee.initial_evaluation_completed_detail')).replace('$X', formatDateTime(balancedScorecardDetails?.initial_evaluation_completed_at))
                }} />
              </ReactTooltip>

              {
                !isStateEmployee && <Button variant="secondary" data-tooltip-id={evaluationCompleted ? 'evaluation-completed' : ''} onClick={(e) => handleBtnClickBlur(e, () => handleChangePreviewMode('complete_evaluation'))} disabled={readOnlyMode || weightingErrorTranslationKey} data-testid="evaluation-tab-button" className="mr-2">
                  <i className="bi bi-file-earmark-check"></i> {editableTranslate('balanced_scorecard.evaluation_tab_button')}
                </Button>
              }
              <ReactTooltip id={`evaluation-completed`} place="bottom-end">
                {editableTranslate('balanced_scorecard.rating_card_completed_detail', 'Leistungskarte abgeschlossen am')} <span className="font-weight-bold">{formatDateTime(balancedScorecardDetails?.evaluation_completed_at)}</span>.
                <br /> {editableTranslate('balanced_scorecard.contact_app_team', 'Bitte wenden Sie sich an das Applikationsteam, um diese Leistungskarte wieder freizugeben.')}
                {/* Leistungskarte abgeschlossen am {formatDateTime(balancedScorecardDetails?.evaluation_completed_at)}.<br /> Bitte wenden Sie sich an das Applikationsteam, um diese Leistungskarte wieder freizugeben. */}
              </ReactTooltip>

              {
                (!isStateEmployee || evaluationCompleted) && <Button variant="secondary" onClick={(e) => handleBtnClickBlur(e, () => handleChangePreviewMode('report'))} disabled={(isStateEmployee && !balancedScorecardDetails?.evaluation_completed_at)} data-tooltip-id={(isStateEmployee && !balancedScorecardDetails?.evaluation_completed_at) ? 'state-employee-evaluation-not-completed' : ''} data-testid="report-button">
                  <i className="bi bi-file-text"></i> {editableTranslate(isStateEmployee ? 'state_employee.evaluation_report_button' : 'balanced_scorecard.report_tab_button')}
                </Button>
              }

              <ReactTooltip id="state-employee-evaluation-not-completed" place="bottom-end" content={translate('state_employee.evaluation_not_completed')} />

              <ReactTooltip id="state-employee-implemented-later" place="bottom-end" content={translate('state_employee.implemented_later')} />

              <ReactTooltip id="state-employee-interview-not-allowed" place="bottom-end" content={translate('state_employee.interview_not_allowed')} />
            </div>
          </div>
        }

        <div className="mt-3">
          {
            lkDetailsFetched &&
            <div className="general-info">
              <div className="row row-cols-6">
                <div className="col">
                  <span>{editableTranslate('balanced_scorecard.employed')}</span>
                  <span className="info-field-value">{employee ? `${employee.first_name} ${employee.last_name}` : 'Bartels. Chantal'}</span>
                </div>
                {
                  filteredFields.filter(f => !['last_name', 'first_name'].includes(f.key)).map((field, i) => {
                    let val = '';

                    if (!employee || !balancedScorecardDetails) {
                      val = samples[field.key] || 'Value';
                    } else {
                      val = ['missing_months', 'missing_days'].includes(field.key)
                        ? employee?.[field.key]
                        : employee?.[field.key] || employee.information?.[field.name] || employee.information?.[field.slug];

                      if (['annual_compensation', 'individual_pay_factor', 'working_days_per_week'].includes(field.key) && val) {
                        val = formatMoney(val);
                      } else if (field.key === 'missing_months' && val !== null && val !== undefined && val !== '') {
                        val = formatMoney(val);
                      }
                    }

                    return <div key={i} className="col">
                      <span className="break-word">{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                      <span className="info-field-value">
                        {
                          (field.is_balanced_scorecard_specific && balancedScorecardDetails) ?
                            <BalancedScorecardFieldValue employee={employee} bs={balancedScorecardDetails} field={field} forTable={false} softwareSettings={softwareSettings} /> :
                            val
                        }
                      </span>
                    </div>
                  })
                }
                {
                  (employee && isStateEmployee && ((!isSecondaryEvaluator && customConfig?.secondary_evaluation_enabled !== false && stateEvaluationFlow?.flow !== 'primary_evaluator_only') || (isSecondaryEvaluator && customConfig?.initial_evaluation_enabled !== false))) && <div className="col">
                    <span>{editableTranslate(isSecondaryEvaluator ? 'state_employee.first_evaluator' : 'state_employee.second_evaluator', isSecondaryEvaluator ? 'Erstbeurteiler*in' : 'Zweitbeurteiler*in')}</span>
                    <span className="info-field-value">
                      {
                        isSecondaryEvaluator ? firstEvaluatorName : secondaryEvaluatorName
                      }
                    </span>
                  </div>
                }
              </div>
              {
                (showFeedback || showDirection) && <hr />
              }
              {
                showFeedback && <div>
                  <span className="font-weight-bold">{editableTranslate(isStateEmployee ? 'state_employee.rating_notes' : 'balanced_scorecard.notes')}
                    {
                      (!readOnlyMode && !editFeedback && !isSecondaryEvaluator) && <Button variant="ghost" size="inline" icon="bi-pencil-square" onClick={() => setEditFeedback(true)} data-testid="edit-button">{editableTranslate('global.edit')}</Button>
                    }
                  </span>
                  {
                    !editFeedback ? <ExpandableHtml html={feedback || '-'} className="fade-gray-bg" /> :
                      <>
                        <EditorInput data-testid="feedback-input"
                          className={`mt-2 ${readOnlyMode ? 'hide-toolbar' : ''}`}
                          value={feedback}
                          onChangeCallback={(v) => setFeedback(v)}
                          validate={(v) => v ? setFeedbackError(true) : setFeedbackError(false)}
                          readOnly={readOnlyMode} />

                        <div className="text-right mt-2">
                          <Button variant="secondary" onClick={handleCancelFeedback} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                          <Button variant="primary" onClick={handleSaveFeedback} data-testid="save-button">{editableTranslate('button.save')}</Button>
                        </div>
                      </>
                  }
                </div>
              }
              {
                showDirection && <div className={showFeedback ? 'mt-4' : ''}>
                  <span className="font-weight-bold">{editableTranslate('balanced_scorecard.directions_button')}</span>
                  <ExpandableHtml html={settings?.balancedScorecardNotes || '-'} className="mt-2 fade-gray-bg" />
                </div>
              }
            </div>
          }
        </div>

        {
          (error && (!weightingErrorTranslationKey || error?.translationKey !== weightingErrorTranslationKey)) && <div data-testid="scroll-to-error"
            ref={errorRef}
            onClick={errorField && previewMode !== 'interview' ? scrollToErrorField : null}
            className={`${errorField && previewMode !== 'interview' ? 'sticky z-2 pt-3 bg-white cursor-pointer' : ''} mt-4 mb-3`}>
            <ErrorAlert error={error} onClose={handleCloseError} isHtml={true} />
          </div>
        }

        {
          (employee && !balancedScorecardDetails) && <div className="d-flex justify-content-center mt-4 mb-3" data-testid="loading">
            <div className="spinner-border" role="status">
              <span className="sr-only">Wird geladen...</span>
            </div>
          </div>
        }

        {
          (!employee || balancedScorecardDetails) && <>
            {
              previewMode === 'state_employee_data' && <StateEmployeeDataMode
                employee={scorecardContextEmployee}
                setStateEmployeeDirty={setStateEmployeeDirty}
                fetchEmployee={fetchEmployee}
                forceReadOnly={reviewerView || isSecondaryEvaluator || balancedScorecardDetails?.initial_evaluation_completed_at || (scorecardContextFrozen && !scorecardContextEditable)}
                callback={() => fetchBalancedScorecards(false)}
                settingIdentifier={balancedScorecardDetails?.balanced_scorecard_setting?.slug}
                scorecardHashId={balancedScorecardDetails?.hash_id}
                persistToScorecardContext={scorecardContextFrozen}
              />
            }
            {
              previewMode === 'complete_evaluation' && <div>
                <hr />
                <div className="complete-evaluation-div">
                  {
                    !isStateEmployee && <>
                      <h5>{editableTranslate('balanced_scorecard.complete_title')}</h5>
                      <ul>
                        <li>{editableTranslate('balanced_scorecard.complete_list_1')}</li>
                        <li>{editableTranslate('balanced_scorecard.complete_list_2')}</li>
                      </ul>
                    </>
                  }
                  {
                    isStateEmployee && !isSecondaryEvaluator && <>
                      <h5>{editableTranslate('state_employee.initial_complete_title')}</h5>
                      <ul>
                        <li>{editableTranslate('state_employee.initial_complete_list_1')}</li>
                        <li>{editableTranslate('state_employee.initial_complete_list_2')}</li>
                      </ul>
                    </>
                  }
                  {
                    isStateEmployee && isSecondaryEvaluator && <>
                      <h5>{editableTranslate('state_employee.secondary_complete_title')}</h5>
                      <ul>
                        <li>{editableTranslate('state_employee.secondary_complete_list_1')}</li>
                        <li>{editableTranslate('state_employee.secondary_complete_list_2')}</li>
                      </ul>
                    </>
                  }
                  <div className="text-right">
                    <Button variant="secondary" onClick={() => handleChangePreviewMode('evaluation')} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                    <Button variant="primary" onClick={handleCompleteEvaluation} data-testid="proceed-button" className="ml-2">{editableTranslate('button.proceed')}</Button>
                  </div>
                </div>
              </div>
            }
            {
              previewMode === 'report' && <div>
                <div className="balanced-scorecard-report-div d-flex justify-content-between align-items-center mt-4">
                  <h5 className="mb-0">{editableTranslate(isStateEmployee ? 'state_employee.evaluation_report_button' : 'balanced_scorecard.report_tab_button')}</h5>

                  {
                    defaultLogoExist && <div className="d-flex align-items-center">
                      {
                        !isStateEmployee && <>
                          <div className="d-flex align-items-center">
                            <i data-tooltip-id="btn-report-without" className="bi bi-file-earmark-minus text-bigger mr-1"></i>
                            <ReactTooltip id={`btn-report-without`} place="bottom">{translate('tooltips.balanced_scorecard.report_without_objective')}</ReactTooltip>
                            <span className="toggle-switch">
                              <input id="report-switch" type="checkbox" checked={downloadFull} onChange={e => setDownloadFull(e.target.checked)} disabled={pdfLoading} data-testid="report-switch-checkbox" />
                              <label htmlFor="report-switch"></label>
                            </span>
                            <i data-tooltip-id="btn-report-with" className="bi bi-file-earmark-text text-bigger ml-1"></i>
                            <ReactTooltip id={`btn-report-with`} place="bottom">{translate('tooltips.balanced_scorecard.report_with_objective')}</ReactTooltip>
                          </div>
                          <span className="mx-3">|</span>
                        </>
                      }
                      <div>
                        <Button variant="primary" onClick={() => handlePdfReport(false)} loading={pdfLoading === 'download'} disabled={!!pdfLoading} data-testid="download-report-button" className="mr-2">{pdfLoading !== 'download' && <i className="bi bi-download"></i>} {editableTranslate('balanced_scorecard.report_download')}</Button>

                        {
                          allowReportEmail && <Button variant="primary" onClick={() => handlePdfReport(true)} loading={pdfLoading === 'email'} disabled={!!pdfLoading} data-testid="email-report-button">{pdfLoading !== 'email' && <i className="bi bi-envelope"></i>} {editableTranslate('balanced_scorecard.report_send_via_email')}</Button>
                        }
                      </div>
                    </div>
                  }
                </div>
                <hr />

                {
                  defaultLogoExist === null && <div data-testid="loading">
                    <div className="spinner-border spinner-border-sm mr-3" role="status">
                      <span className="sr-only">Wird geladen...</span>
                    </div>
                  </div>
                }

                {
                  defaultLogoExist === false && <div>
                    <ErrorAlert error={{ translationKey: 'evaluation.report_logo_not_found' }} />
                  </div>
                }
              </div>
            }
            {
              (previewMode === 'evaluation' && !isExcluded) && <>
                {
                  (balancedScorecardDetails?.rating_mode === 'normal_rating' || !balancedScorecard) && <>
                    <EvaluationMode
                      settings={settings}
                      balancedScorecardDetails={balancedScorecardDetails}
                      saveAgreement={saveAgreement}
                      evaluationCompleted={evaluationCompleted}
                      weight={weight}
                      assignedTeamObjectives={assignedTeamObjectives}
                      individualObjectives={individualObjectives}
                      reviewerView={reviewerView}
                      customConfigFetched={customConfigFetched}
                      checkConfig={checkConfig}
                      evaluationDirty={evaluationDirty}
                      setEvaluationDirty={setEvaluationDirty}
                      levels={levels}
                      extremeLevels={extremeLevels}
                      individualResultCommentMandatory={individualResultCommentMandatory}
                      systematicResultCommentMandatory={systematicResultCommentMandatory}
                      balancedScorecardMetas={balancedScorecardMetas}
                      weightingErrorTranslationKey={weightingErrorTranslationKey}
                      stateEvaluationFlow={stateEvaluationFlow}
                      stateCompleteEvaluationButton={isStateEmployee ? <Button variant="secondary" data-tooltip-id={(balancedScorecardDetails?.evaluation_completed_at || (!isSecondaryEvaluator && initialEvaluationCompleted)) ? 'state-employee-evaluation-completed' : ''} onClick={(e) => handleBtnClickBlur(e, () => handleChangePreviewMode('complete_evaluation'))} disabled={readOnlyMode || isExcluded || (!isSecondaryEvaluator && initialEvaluationCompleted) || (isSecondaryEvaluator && !initialEvaluationCompleted)} data-testid="evaluation-tab-button" className="ml-2">
                        <i className="bi bi-file-earmark-check"></i> {editableTranslate('balanced_scorecard.evaluation_tab_button')}
                      </Button> : <></>}
                      handleChangePreviewMode={handleChangePreviewMode}
                      validateAll={validateAll}
                      errorField={errorField}
                      setErrorField={setErrorField} />
                  </>
                }
              </>
            }
            {
              (previewMode === 'agreement' && !isExcluded) && <AgreementMode
                settings={settings}
                balancedScorecardDetails={balancedScorecardDetails}
                fetchBalancedScorecardDetails={fetchBalancedScorecardDetails}
                saveAgreement={saveAgreement}
                evaluationCompleted={evaluationCompleted}
                setError={setError}
                assignedTeamObjectives={assignedTeamObjectives}
                individualObjectives={individualObjectives}
                reviewerView={reviewerView}
                setAgreementDirty={setAgreementDirty}
                weightingErrorTranslationKey={weightingErrorTranslationKey}
                errorField={errorField}
                isModal={isModal} />
            }

            {
              (previewMode === 'interview' && !isExcluded) && <InterviewMode balancedScorecardDetails={balancedScorecardDetails} fetchBalancedScorecards={fetchBalancedScorecards} fetchBalancedScorecardDetails={fetchBalancedScorecardDetails} reviewerView={reviewerView} setInterviewDirty={setInterviewDirty} interviews={interviewsData} fetchInterviews={fetchInterviewsData} complaints={complaints} fetchComplaints={fetchComplaints} isSecondaryEvaluator={isSecondaryEvaluator} stateEvaluationFlow={stateEvaluationFlow} />
            }

            {
              employee?.exclude_from_payout && <div className="my-5 mx-auto max-w-500">
                <div className="d-flex flex-column exclusion-div">
                  <p className="font-weight-bold text-big"><i className="bi bi-exclamation-triangle-fill text-bigger"></i> {editableTranslate('evaluation.excluded_description')}</p>

                  <div className="exclusion-reason-div">
                    <p>
                      <span className="font-weight-bold">{editableTranslate('evaluation.excluded_reason')}: </span>
                      <span>{employee?.exclude_from_payout_reason || '-'}</span>
                    </p>
                  </div>
                </div>
              </div>
            }

            {
              balancedScorecardDetails?.rating_mode === 'average_value_percentile' && <div className="my-5 mx-auto max-w-500">
                <div className="d-flex flex-column exclusion-div">
                  <p className="font-weight-bold text-big"><i className="bi bi-exclamation-triangle-fill"></i> {editableTranslate('evaluation.average_value_percentile_description')}</p>

                  <div className="exclusion-reason-div">
                    <p>
                      <span className="font-weight-bold">{editableTranslate('evaluation.excluded_reason')}: </span>
                      <span>{editableTranslate('evaluation.average_value_percentile_reason')}</span>
                    </p>
                  </div>
                </div>
              </div>
            }

            {
              previewMode === 'import_confirmation' && <div>
                <hr />
                <div className="complete-evaluation-div">
                  <h5>{editableTranslate('balanced_scorecard.import_objectives')}</h5>

                  {
                    !displayImportOptions && <div>
                      <p className="mt-3 mb-2">{editableTranslate('balanced_scorecard.import_objectives_question')}</p>
                      <ul>
                        <li>{editableTranslate('balanced_scorecard.import_objectives_question_list_1')}</li>
                        <li>{editableTranslate('balanced_scorecard.import_objectives_question_list_2')}</li>
                      </ul>
                      <p className="mt-2">{editableTranslate('balanced_scorecard.import_objectives_question_note')}</p>

                      <div className="text-right">
                        <Button variant="secondary" onClick={handleImportNo} disabled={reviewerView} data-testid="cancel-button">{editableTranslate('form.no')}</Button>
                        <Button variant="primary" onClick={handleImportYes} disabled={reviewerView} data-testid="confirm-button" className="ml-2">{editableTranslate('form.yes')}</Button>
                      </div>
                    </div>
                  }
                  {
                    displayImportOptions && <div>
                      <p className="mt-3 mb-2">{editableTranslate('balanced_scorecard.import_objectives_instruction')}</p>
                      {
                        !importObjectives.hasOwnProperty('individual_objectives') && <div className="spinner-border" role="status">
                          <span className="sr-only">Wird geladen...</span>
                        </div>
                      }

                      {
                        importObjectives.hasOwnProperty('individual_objectives') && (importObjectivesSynced.individual_objectives.length === 0 && importObjectivesSynced.team_objectives.length === 0 && importObjectivesSynced.systematic_objectives.length === 0) && <div>
                          <hr />
                          <p className="mb-0">{editableTranslate('balanced_scorecard.import_no_objectives')}</p>
                        </div>
                      }

                      {
                        (importObjectivesSynced.individual_objectives.length > 0 || importObjectivesSynced.team_objectives.length > 0) && <hr />
                      }
                      {
                        importObjectivesSynced.individual_objectives.length > 0 && importObjectivesSynced.individual_objectives.map((item, i) => <div key={i}>
                          <div className="import-option-div import-individual-div">
                            <span><img src={personIcon} alt="Person icon" /> {item.name}</span>
                            <Button variant="ghost" size="inline" icon="bi-x-lg" onClick={() => handleRemoveImportObjective('individual', item.hash_id)} data-testid={`remove-import-objective-${item.hash_id}`} />
                          </div>
                        </div>)
                      }
                      {
                        importObjectivesSynced.team_objectives.length > 0 && importObjectivesSynced.team_objectives.map((item, i) => <div key={i}>
                          <div className="import-option-div import-team-div">
                            <span><img src={peopleIcon} alt="People icon" /> {item.name}</span>
                            <Button variant="ghost" size="inline" icon="bi-x-lg" onClick={() => handleRemoveImportObjective('team', item.hash_id)} data-testid={`remove-import-objective-${item.hash_id}`} />
                          </div>
                        </div>)
                      }

                      {
                        importObjectivesSynced.systematic_objectives.length > 0 && importObjectivesSynced.systematic_objectives.map((item, i) => <div key={i}>
                          {i === 0 && <hr />}
                          <div className="import-option-div import-systematic-div">
                            <span><img src={toolIcon} alt="Tool icon" /> {item.name}</span>
                            <Button variant="ghost" size="inline" icon="bi-x-lg" onClick={() => handleRemoveImportObjective('systematic', item.name)} data-testid={`remove-import-objective-${item.name}`} />
                          </div>
                        </div>)
                      }

                      <div className="text-right mt-5">
                        <Button variant="secondary" onClick={handleImportCancel} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                        <Button variant="primary" onClick={handleImportSave} data-testid="proceed-button" className="ml-2">{editableTranslate('button.proceed')}</Button>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </>
        }
      </div>
    }
    {
      viewMode === 'compact' && <div className="balanced-scorecard-content">
        {
          error && <div data-testid="scroll-to-error"
            ref={errorRef}
            onClick={errorField && previewMode !== 'interview' ? scrollToErrorField : null}
            className={`${errorField && previewMode !== 'interview' ? 'sticky z-2 pt-3 bg-white cursor-pointer' : ''} mb-3`}>
            <ErrorAlert error={error} onClose={handleCloseError} isHtml={true} />
          </div>
        }

        {
          (employee && !balancedScorecardDetails) && <div className="d-flex justify-content-center mt-4 mb-3" data-testid="loading">
            <div className="spinner-border" role="status">
              <span className="sr-only">Wird geladen...</span>
            </div>
          </div>
        }

        {
          balancedScorecardDetails && <>
            <div className="row" ref={compactRowRef}>
              <div className="col-12 d-none hidden-compact-div"></div>

              <StateEmployeeDataCompactReadOnly
                employee={scorecardContextEmployee}
                settingIdentifier={balancedScorecardDetails?.balanced_scorecard_setting?.slug}
                isSecondaryEvaluator={isSecondaryEvaluator}
                firstEvaluatorName={firstEvaluatorName}
                secondaryEvaluatorName={secondaryEvaluatorName}
                stateEvaluationFlow={stateEvaluationFlow}
                customConfig={customConfig}
                setCompactDataLoaded={setCompactDataLoaded} />

              {
                compactDataLoaded && <div className="col-6 mb-4">
                  <div className="col-compact">
                    <h5>{editableTranslate('state_employee.individual_objectives', 'Einzelmerkmale')}</h5>
                    <div className="gray-box-2 px-3 py-2">
                      <StateEmployeeObjectivesCompactReadOnly
                        settings={settings}
                        balancedScorecardDetails={balancedScorecardDetails}
                        levels={levels}
                        initialEvaluationCompleted={initialEvaluationCompleted} />
                    </div>
                  </div>
                </div>
              }
            </div>

            {
              (!isSecondaryEvaluator || initialEvaluationCompleted) && <>
                <EvaluationMode
                  settings={settings}
                  balancedScorecardDetails={balancedScorecardDetails}
                  saveAgreement={saveAgreement}
                  evaluationCompleted={evaluationCompleted}
                  weight={weight}
                  assignedTeamObjectives={assignedTeamObjectives}
                  individualObjectives={individualObjectives}
                  reviewerView={reviewerView}
                  customConfigFetched={customConfigFetched}
                  checkConfig={checkConfig}
                  evaluationDirty={evaluationDirty}
                  setEvaluationDirty={setEvaluationDirty}
                  levels={levels}
                  extremeLevels={extremeLevels}
                  individualResultCommentMandatory={individualResultCommentMandatory}
                  systematicResultCommentMandatory={systematicResultCommentMandatory}
                  balancedScorecardMetas={balancedScorecardMetas}
                  stateEvaluationFlow={stateEvaluationFlow}
                  hideObjectives={true}
                  previewMode={previewMode}
                  handleChangePreviewMode={handleChangePreviewMode}
                  handleBtnClickBlur={handleBtnClickBlur}
                  handleCompleteEvaluation={handleCompleteEvaluation}
                  completeLoading={completeLoading}
                  validateAll={validateAll}
                  interviewEnabled={interviewEnabled}
                  interviews={interviews}
                  canOpenInterviewTab={canOpenInterviewTab}
                  fetchBalancedScorecards={fetchBalancedScorecards}
                  fetchBalancedScorecardDetails={fetchBalancedScorecardDetails}
                  setInterviewDirty={setInterviewDirty}
                  interviewsData={interviewsData}
                  fetchInterviews={fetchInterviewsData}
                  complaints={complaints}
                  fetchComplaints={fetchComplaints}
                  errorField={errorField}
                  setErrorField={setErrorField} />

                <ReactTooltip id={`state-employee-evaluation-completed`} place="bottom-end">
                  <span dangerouslySetInnerHTML={{
                    __html: sanitize(balancedScorecardDetails?.evaluation_completed_at ?
                      editableTranslateString('state_employee.rating_card_completed_detail').replace('$X', formatDateTime(balancedScorecardDetails?.evaluation_completed_at)) :
                      editableTranslateString('state_employee.initial_evaluation_completed_detail')).replace('$X', formatDateTime(balancedScorecardDetails?.initial_evaluation_completed_at))
                  }} />
                </ReactTooltip>

                <ReactTooltip id="state-employee-evaluation-not-completed" place="bottom-end" content={translate('state_employee.evaluation_not_completed')} />

                <ReactTooltip id="state-employee-implemented-later" place="bottom-end" content={translate('state_employee.implemented_later')} />

                {
                  previewMode === 'report' && <div className="mt-3">
                    {
                      defaultLogoExist === null && <div className="text-right" data-testid="loading">
                        <div className="spinner-border spinner-border-sm mr-3" role="status">
                          <span className="sr-only">Wird geladen...</span>
                        </div>
                      </div>
                    }
                    {
                      defaultLogoExist === false && <div>
                        <ErrorAlert error={{ translationKey: 'evaluation.report_logo_not_found' }} onClose={() => handleChangePreviewMode('evaluation')} />
                      </div>
                    }
                    {
                      defaultLogoExist && <div className="d-flex align-items-center justify-content-end">
                        <Button variant="primary" onClick={() => handlePdfReport(false)} loading={pdfLoading === 'download'} disabled={!!pdfLoading} data-testid="download-report-button" className="mr-2">
                          {pdfLoading !== 'download' && <i className="bi bi-download"></i>} {editableTranslate('balanced_scorecard.report_download')}
                        </Button>

                        {
                          allowReportEmail && <Button variant="primary" onClick={() => handlePdfReport(true)} loading={pdfLoading === 'email'} disabled={!!pdfLoading} data-testid="email-report-button">
                            {pdfLoading !== 'email' && <i className="bi bi-envelope"></i>} {editableTranslate('balanced_scorecard.report_send_via_email')}
                          </Button>
                        }
                      </div>
                    }
                  </div>
                }
              </>
            }
          </>
        }
      </div>
    }
  </>
};
